import { useMemo } from 'react';
import styled from '@emotion/styled';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const COLORS = ['#1976D2', '#00B8D4', '#5C6BC0', '#8BC34A', '#880E4F', '#006A71', '#1E2D3D', '#E0E4E8'];

const ChartBox = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 16px;
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
  margin: 0 0 12px 0;
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 16px;
`;

interface TrendRow {
  quarter: string;
  molecule: string;
  starts: number;
}

function TrendsChart({ data }: { data: TrendRow[] | undefined }) {
  const { pivoted, molecules } = useMemo(() => {
    if (!data?.length) return { pivoted: [] as Record<string, unknown>[], molecules: [] as string[] };
    const molTotals: Record<string, number> = {};
    data.forEach(({ molecule, starts }) => {
      molTotals[molecule] = (molTotals[molecule] || 0) + starts;
    });
    const allMols = Object.entries(molTotals)
      .sort((a, b) => b[1] - a[1])
      .map(([m]) => m);
    const byQuarter: Record<string, Record<string, unknown>> = {};
    data.forEach(({ quarter, molecule, starts }) => {
      if (!byQuarter[quarter]) byQuarter[quarter] = { quarter };
      byQuarter[quarter][molecule] = starts;
    });
    return {
      pivoted: Object.values(byQuarter).sort((a, b) => String(a.quarter).localeCompare(String(b.quarter))),
      molecules: allMols,
    };
  }, [data]);

  if (!pivoted.length) {
    return <p style={{ color: '#ADADAD', fontSize: 12, textAlign: 'center', padding: '32px 0' }}>No trend data available</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={pivoted} margin={{ left: 0, right: 10 }}>
        <XAxis dataKey="quarter" tick={{ fill: '#626262', fontSize: 9 }} interval="preserveStartEnd" />
        <YAxis tick={{ fill: '#626262', fontSize: 10 }} />
        <Tooltip
          contentStyle={{ background: '#fff', border: '1px solid #e5e5e5', fontSize: 11, borderRadius: 6 }}
        />
        <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
        {molecules.map((mol, i) => (
          <Line
            key={mol}
            type="monotone"
            dataKey={mol}
            stroke={COLORS[i % COLORS.length]}
            dot={false}
            strokeWidth={2}
            connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function RegimenSharesChart({ data }: { data: { name: string; share: number }[] | undefined }) {
  if (!data?.length) {
    return <p style={{ color: '#ADADAD', fontSize: 12, textAlign: 'center', padding: '32px 0' }}>No regimen data available</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ left: 0, right: 10, bottom: 60 }}>
        <XAxis dataKey="name" tick={{ fill: '#626262', fontSize: 9 }} angle={-45} textAnchor="end" />
        <YAxis tick={{ fill: '#626262', fontSize: 10 }} />
        <Tooltip
          contentStyle={{ background: '#fff', border: '1px solid #e5e5e5', fontSize: 11, borderRadius: 6 }}
          formatter={(value: number) => `${(value * 100).toFixed(1)}%`}
        />
        <Bar dataKey="share" fill={COLORS[0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function VolumeHistogramChart({ data }: { data: { bin_start: number; count: number }[] | undefined }) {
  if (!data?.length) {
    return <p style={{ color: '#ADADAD', fontSize: 12, textAlign: 'center', padding: '32px 0' }}>No volume data available</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ left: 0, right: 10 }}>
        <XAxis dataKey="bin_start" tick={{ fill: '#626262', fontSize: 9 }} />
        <YAxis tick={{ fill: '#626262', fontSize: 10 }} />
        <Tooltip
          contentStyle={{ background: '#fff', border: '1px solid #e5e5e5', fontSize: 11, borderRadius: 6 }}
        />
        <Bar dataKey="count" fill={COLORS[1]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function FormulationPieChart({ data }: { data: { name: string; pct: number }[] | undefined }) {
  if (!data?.length) {
    return <p style={{ color: '#ADADAD', fontSize: 12, textAlign: 'center', padding: '32px 0' }}>No formulation data available</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie
          data={data}
          dataKey="pct"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={80}
          label={(entry) => `${entry.name}: ${(entry.pct * 100).toFixed(1)}%`}
        >
          {data.map((_, idx) => (
            <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(value: number) => `${(value * 100).toFixed(1)}%`} />
      </PieChart>
    </ResponsiveContainer>
  );
}

interface EDAChartsProps {
  charts: Record<string, unknown>;
}

export function EDACharts({ charts }: EDAChartsProps) {
  const regimenShares = charts.regimen_shares as { name: string; share: number }[] | undefined;
  const hcpVolHist = charts.hcp_volume_histogram as { bin_start: number; count: number }[] | undefined;
  const quarterlyTrends = charts.quarterly_trends as TrendRow[] | undefined;
  const formDist = charts.formulation_distribution as { name: string; pct: number }[] | undefined;
  const groupDist = charts.group_type_distribution as { name: string; pct: number }[] | undefined;

  return (
    <ChartGrid>
      {regimenShares && (
        <ChartBox>
          <ChartTitle>Regimen Shares</ChartTitle>
          <RegimenSharesChart data={regimenShares} />
        </ChartBox>
      )}

      {hcpVolHist && (
        <ChartBox>
          <ChartTitle>HCP Volume Distribution</ChartTitle>
          <VolumeHistogramChart data={hcpVolHist} />
        </ChartBox>
      )}

      {quarterlyTrends && (
        <ChartBox>
          <ChartTitle>Quarterly Trends</ChartTitle>
          <TrendsChart data={quarterlyTrends} />
        </ChartBox>
      )}

      {formDist && (
        <ChartBox>
          <ChartTitle>Formulation Distribution</ChartTitle>
          <FormulationPieChart data={formDist} />
        </ChartBox>
      )}

      {groupDist && (
        <ChartBox>
          <ChartTitle>Group Type Distribution</ChartTitle>
          <FormulationPieChart data={groupDist} />
        </ChartBox>
      )}
    </ChartGrid>
  );
}
